import {
  migrateKurdishTalentCmsAuth,
  KT_CMS_TOKEN_KEY,
} from "../../utils/authStorageKeys";
import { app } from "./firebase";
import {
  getStorage,
  ref,
  deleteObject,
  refFromURL,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import uuid from "react-uuid";

export const ApiBase =
  process.env.REACT_APP_API_BASE ||
  "https://kurdishtalent-api-48516816480.europe-north2.run.app/";
export const ApiBase_slashless =
  `${String(ApiBase).replace(/\/$/, "").replace(/^http:/i, "https:")}/api/uploaded_media`;

migrateKurdishTalentCmsAuth();

var default_language_id = 3;

function createCmsJsonHeaders(includeAuth = true) {
  migrateKurdishTalentCmsAuth();
  const h = new Headers();
  h.append("Content-Type", "application/json");
  h.append("Accept", "application/json");
  if (includeAuth) {
    const t = localStorage.getItem(KT_CMS_TOKEN_KEY);
    if (t) h.append("Authorization", `Bearer ${t}`);
  }
  return h;
}

function createCmsFileHeaders(includeAuth = true) {
  migrateKurdishTalentCmsAuth();
  const h = new Headers();
  h.append("Accept", "application/json");
  if (includeAuth) {
    const t = localStorage.getItem(KT_CMS_TOKEN_KEY);
    if (t) h.append("Authorization", `Bearer ${t}`);
  }
  return h;
}

/** Server-side: copy file from another gallery row onto `targetId` (avoids browser GET on media URLs). */
export async function replaceVideoGalleryFileFrom(targetId, sourceId) {
  migrateKurdishTalentCmsAuth();
  const h = new Headers();
  h.append("Content-Type", "application/json");
  h.append("Accept", "application/json");
  const t = localStorage.getItem(KT_CMS_TOKEN_KEY);
  if (t) h.append("Authorization", `Bearer ${t}`);
  const response = await fetch(
    `${ApiBase}postapi/videogallery/${targetId}/replace-file-from/`,
    {
      headers: h,
      method: "POST",
      body: JSON.stringify({ source_id: sourceId }),
    }
  );
  let data = {};
  try {
    data = await response.json();
  } catch (_) {
    /* non-JSON */
  }
  if (!response.ok) {
    const err = new Error(
      typeof data.detail === "string" ? data.detail : "Replace failed"
    );
    err.body = data;
    throw err;
  }
  return data;
}

/** Server-side: new gallery row with a copy of `sourceId`'s file. */
export async function createVideoGalleryCopyFrom(sourceId, extra = {}) {
  migrateKurdishTalentCmsAuth();
  const h = new Headers();
  h.append("Content-Type", "application/json");
  h.append("Accept", "application/json");
  const t = localStorage.getItem(KT_CMS_TOKEN_KEY);
  if (t) h.append("Authorization", `Bearer ${t}`);
  const response = await fetch(`${ApiBase}postapi/videogallery/create-copy/`, {
    headers: h,
    method: "POST",
    body: JSON.stringify({ source_id: sourceId, ...extra }),
  });
  let data = {};
  try {
    data = await response.json();
  } catch (_) {
    /* non-JSON */
  }
  if (!response.ok) {
    const err = new Error(
      typeof data.detail === "string" ? data.detail : "Create failed"
    );
    err.body = data;
    throw err;
  }
  return data;
}

function parseApiErrorDetail(body) {
  const d = body && body.detail;
  if (d == null) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((item) =>
        typeof item === "string"
          ? item
          : item &&
            (item.string || item.msg || item.message || JSON.stringify(item))
      )
      .filter(Boolean)
      .join(" ");
  }
  if (typeof d === "object")
    return d.string || d.msg || d.message || JSON.stringify(d);
  return String(d);
}

/**
 * Upload a talent video to Firebase Storage (GCP).
 * Resolves to { ok: true, public_url } or { ok: false, error }.
 */
export function uploadTalentVideoToFirebase(file, onProgress) {
  return new Promise((resolve) => {
    try {
      const ext = (file?.name && String(file.name).split(".").pop()) || "mp4";
      const objectPath = `videos/${uuid()}.${ext}`;
      const storage = getStorage(app);
      const storageRef = ref(storage, objectPath);
      const task = uploadBytesResumable(storageRef, file, {
        contentType: file?.type || "video/mp4",
      });
      task.on(
        "state_changed",
        (snapshot) => {
          if (typeof onProgress === "function" && snapshot.totalBytes > 0) {
            onProgress(
              Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            );
          }
        },
        (error) => {
          resolve({
            ok: false,
            error: (error && error.message) || "Video upload failed",
          });
        },
        async () => {
          try {
            const public_url = await getDownloadURL(task.snapshot.ref);
            if (typeof onProgress === "function") onProgress(100);
            resolve({ ok: true, public_url });
          } catch (e) {
            resolve({
              ok: false,
              error: (e && e.message) || "Could not get download URL",
            });
          }
        }
      );
    } catch (e) {
      resolve({
        ok: false,
        error: (e && e.message) || "Video upload failed",
      });
    }
  });
}

export async function login(userinfo) {
  const response = await fetch(`${ApiBase}auth/login/`, {
    headers: createCmsJsonHeaders(false),
    method: "POST",
    body: JSON.stringify(userinfo),
  });

  const data = await response.json();

  return data;
}

export async function loadProfile() {
  const response = await fetch(`${ApiBase}auth/profiles/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results[0];
  }
}

export async function loadMyProfile() {
  const response = await fetch(`${ApiBase}auth/profile/me/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadProfileBuId(id) {
  const response = await fetch(`${ApiBase}auth/profiles/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results[0];
  }
}
export async function loadProfiles() {
  const response = await fetch(`${ApiBase}auth/profiles/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}
export async function loadDegreeReport(filter_query) {
  const response = await fetch(`${ApiBase}rep/degreesummary${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadDegrees(courseparticipant) {
  var query=""
  if(courseparticipant)
    query = `?courseparticipant=${courseparticipant}`
  const response = await fetch(`${ApiBase}api/degrees/${query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();
  
  if (data.results) {
    return data.results;
  }
}


export async function loadPeopleDegrees(course,usertalent) {
  var query="?id>0"
  if(course)
    query += `&course=${course}`
    if(course)
    query += `&usertalent=${usertalent}`
  
  const response = await fetch(`${ApiBase}api/peopledegree/${query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();
  
    return data;
  
}

export async function loadCourseDocuments(filter_query) {
  const response = await fetch(
    `${ApiBase}api/coursedocumentadmin/${filter_query}`,
    {
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}

export async function updateCourseDocumentRefereeVisible(id, referee_visible) {
  let body = {
    referee_visible: referee_visible,
  };
  const response = await fetch(`${ApiBase}api/coursedocument/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}


export async function updateCourseDocumentFavorite(id, favorite) {
  let body = {
    favorite : favorite,
  };
  const response = await fetch(`${ApiBase}api/coursedocument/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}


export async function updateCourseDocumentIsPublic(id, is_public) {
  let body = {
    public: is_public,
  };
  const response = await fetch(`${ApiBase}api/coursedocument/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function updateCourseDocumentStar(id, star) {
  let body = {
    star: star,
  };
  const response = await fetch(`${ApiBase}api/coursedocument/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();


  return data;
}

export async function updateNotification(id, is_Read) {
  let body = {
    is_Read: is_Read == "1" ? 1 : 0,
  };
  const response = await fetch(`${ApiBase}api/notifications/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function loadLanguages() {
  const response = await fetch(`${ApiBase}base/language/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}
export async function loadTranslates(search, page = 1, size = 40) {
  const pageNum = Math.max(1, parseInt(page, 10) || 1);
  const sizeNum = Math.min(1000, Math.max(1, parseInt(size, 10) || 40));
  let url = `${ApiBase}api/translate?size=${sizeNum}&page=${pageNum}`;
  if (search && String(search).trim()) {
    url += `&search=${encodeURIComponent(String(search).trim())}`;
  }
  const response = await fetch(url, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadTranslate(id) {
  const response = await fetch(`${ApiBase}api/translate/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadCourseReferees(user_id=null,course=null,page=1,size=30) {
  var query = `?size=${size}&page=${page}`
  if(user_id)
    query+=`&user=${user_id}`
  if(course)
    query+=`&course=${course}`

  const response = await fetch(`${ApiBase}api/coursereferee/${query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function loadCategories() {
  const response = await fetch(`${ApiBase}postapi/category/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadCategory(id) {
  const response = await fetch(`${ApiBase}postapi/category/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadTalents(parent, options = {}) {
  var request = `${ApiBase}api/talent/`;
  const force = Boolean(options && options.force);

  if (!force) {
    const cached = getCachedTalents();
    if (cached) {
      refreshTalentsCache(request);
      return cached;
    }
  }

  const talents = await fetchTalentsList(request);
  setCachedTalents(talents);
  return talents;
}

const TALENTS_CACHE_KEY = "kt_cms_talents_v1";
const TALENTS_CACHE_TTL_MS = 5 * 60 * 1000;

export function getCachedTalents() {
  try {
    const raw = sessionStorage.getItem(TALENTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.data)) return null;
    if (Date.now() - (parsed.ts || 0) > TALENTS_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch (_) {
    return null;
  }
}

function setCachedTalents(talents) {
  try {
    if (Array.isArray(talents)) {
      sessionStorage.setItem(
        TALENTS_CACHE_KEY,
        JSON.stringify({ ts: Date.now(), data: talents })
      );
    }
  } catch (_) {
    /* ignore */
  }
}

export function clearCachedTalents() {
  try {
    sessionStorage.removeItem(TALENTS_CACHE_KEY);
    sessionStorage.removeItem("kt_cms_menu_talents_v1");
  } catch (_) {
    /* ignore */
  }
}

async function fetchTalentsList(request) {
  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });
  return response.json();
}

let talentsRefreshInFlight = null;
function refreshTalentsCache(request) {
  if (talentsRefreshInFlight) return talentsRefreshInFlight;
  talentsRefreshInFlight = fetchTalentsList(request)
    .then((talents) => {
      if (Array.isArray(talents)) setCachedTalents(talents);
      return talents;
    })
    .catch(() => null)
    .finally(() => {
      talentsRefreshInFlight = null;
    });
  return talentsRefreshInFlight;
}

export async function loadMenuTalents(parent) {
  var request = `${ApiBase}api/talent/?mode=menu`;
  const MENU_KEY = "kt_cms_menu_talents_v1";
  try {
    const raw = sessionStorage.getItem(MENU_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        Array.isArray(parsed.data) &&
        Date.now() - (parsed.ts || 0) < TALENTS_CACHE_TTL_MS
      ) {
        fetchTalentsList(request)
          .then((data) => {
            if (Array.isArray(data)) {
              sessionStorage.setItem(
                MENU_KEY,
                JSON.stringify({ ts: Date.now(), data })
              );
            }
          })
          .catch(() => {});
        return parsed.data;
      }
    }
  } catch (_) {
    /* ignore */
  }

  const data = await fetchTalentsList(request);
  try {
    if (Array.isArray(data)) {
      sessionStorage.setItem(
        MENU_KEY,
        JSON.stringify({ ts: Date.now(), data })
      );
    }
  } catch (_) {
    /* ignore */
  }
  return data;
}

export async function loadUserTalents(talent_id, page = 1, query = "") {
  var request = `${ApiBase}api/talent/${talent_id}/usertalents/?page=${page}${query}`; //${talent_id}

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadUserTalentsCount(talent_id) {
  var request = `${ApiBase}api/talent/${talent_id}/usertalents/?is_active=1&is_verified=1`; //${talent_id}

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.count) {
    return data.count;
  } else return 0;
}
export async function loadUserTalentsByUser(user_id) {
  var request = `${ApiBase}api/usertalent/?user=${user_id}`; //${talent_id}

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function loadUserTalentDocuments(usertalent_id) {
  var request = `${ApiBase}api/usertalent/${usertalent_id}/userdocuments/`; //${talent_id}

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function loadUserTalentGallery(usertalent_id) {
  var request = `${ApiBase}api/usertalentgallery/?usertalent=${usertalent_id}`;

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function loadCertificates(user_id) {
  var request = `${ApiBase}api/certificates?user=${user_id}`; //${talent_id}

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function loadTalentDocuments(talent_id) {
  var request = `${ApiBase}api/talent/${talent_id}/documents`; //

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}
export async function loadTalentQuestions(talent_id) {
  var request = `${ApiBase}api/talent/${talent_id}/questions`; //

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function loadUsers(page, size = 30) {
  var page_text = "";
  if (page) page_text = "&page=" + page;
  if (size) page_text = "&size=" + size;
  const response = await fetch(
    `${ApiBase}auth/user?role=participant${page_text}`,
    {
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) {
    return data;
  }
}
export async function getUsersCount(verified=1) {
  const response = await fetch(`${ApiBase}auth/user?role=participant&is_verified=${verified}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.count) {
    return data.count;
  } else return 0;
}
/**
 * Fetches users whose last_online is within the given window.
 * @param {number} minutes – look-back window in minutes (default 5, max 1440)
 * @param {{ minCount?: number }} [options] – if minCount is set, backend returns up to that many users (online in the window first, then most recent activity).
 */
export async function loadOnlineUsers(minutes = 5, options = {}) {
  let qs = `minutes=${minutes}`;
  if (options.minCount != null && options.minCount > 0) {
    qs += `&min_count=${options.minCount}`;
  }
  const response = await fetch(
    `${ApiBase}auth/online/?${qs}`,
    {
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );
  if (!response.ok) return { results: [], count: 0 };
  return response.json();
}

export async function updateLastOnline(userId) {
  await fetch(`${ApiBase}auth/profiles/${userId}/`, {
    method: "PATCH",
    headers: createCmsJsonHeaders(),
    body: JSON.stringify({ last_online: new Date().toISOString() }),
  });
}

export async function filterUsers(query, page,all_roles = false) {
  var role = all_roles ? "" : "&role=participant";
  
  const response = await fetch(
    `${ApiBase}auth/user?is_active=1${role}&page=${page}${query}`,
    {
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  // if (!response.ok) throw new Error(response.statusText);

  if (data.results) {
    return data;
  }
}
export async function filterBannedUsers(query, page) {
  const response = await fetch(
    `${ApiBase}auth/user?role=participant&is_active=0&page=${page}${query}`,
    {
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  // if (!response.ok) throw new Error(response.statusText);

  if (data.results) {
    return data;
  }
}
export async function loadJuries(page, courseId, size = 10) {
  var page_text = "";
  if (page) page_text = "&page=" + page;
  var size_text = "&size=" + (size || 10);
  var course_text = "";
  // if (courseId != null && courseId !== "" && courseId !== "all") {
  //   course_text = "&course=" + encodeURIComponent(courseId);
  // }

  const response = await fetch(
    `${ApiBase}auth/user/?role=referee${page_text}${size_text}${course_text}`,
    {
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data && data.detail) || "Could not load juries");
  }

  return data;
}
export async function loadAudience(page=1,ip,is_verified) {
  var query = `?role=normal&page=${page}&size=50`
  if (is_verified!= null ) query += `&is_verified=${is_verified}`
  if (ip!= null ) query += `&ip=${ip}`

  const response = await fetch(`${ApiBase}auth/user${query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function getJuryCount() {
  const response = await fetch(`${ApiBase}auth/user?role=referee`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.count) return data.count;
  else return 0;
}
export async function loadAdministrators(page) {
  var page_text = "";
  if (page) page_text = "&page=" + page;

  const response = await fetch(`${ApiBase}auth/user?role=admin${page_text}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadEditors(page) {
  var page_text = "";
  if (page) page_text = "&page=" + page;

  const response = await fetch(`${ApiBase}auth/user?role=editor${page_text}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadCourses(page) {
  var page_text = page ? "/?page=" + page + "&status=all" : "/?status=all";

  const response = await fetch(`${ApiBase}api/course${page_text}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}
export async function loadNewestCourse(page) {
  var page_text = page ? "/?page=" + page + "&status=all" : "/?status=all";

  const response = await fetch(`${ApiBase}api/course${page_text}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results && data.results.length>0) {
    return data.results[0];
  }
}

export async function loadPosts(filter_query = "", page) {
  if (filter_query) {
    if (page) filter_query += "&page=" + page;
  } else if (page) filter_query += "?page=" + page;

  console.log(`${ApiBase}postapi/post${filter_query}`);
  const response = await fetch(`${ApiBase}postapi/post${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadPages(filter_query = "", page) {
  if (filter_query) {
    if (page) filter_query += "&page=" + page;
  } else if (page) filter_query += "?page=" + page;

  const response = await fetch(`${ApiBase}postapi/page${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadFaqs(filter_query = "", page) {
  const raw = String(filter_query || "").replace(/^\?/, "");
  const params = new URLSearchParams(raw);
  const pageNum =
    page != null && page !== "" && !Number.isNaN(Number(page))
      ? Math.max(1, Math.floor(Number(page)))
      : Math.max(1, Math.floor(Number(params.get("page")) || 1));
  params.set("page", String(pageNum));
  params.set("size", "50");
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : "";

  const response = await fetch(`${ApiBase}api/faq${suffix}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadPost(id) {
  const response = await fetch(`${ApiBase}postapi/post/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadPage(id) {
  const response = await fetch(`${ApiBase}postapi/page/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadFaq(id) {
  const response = await fetch(`${ApiBase}api/faq/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadGallery(page = 1) {
  const response = await fetch(`${ApiBase}postapi/gallery/?page=${page}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadSetting(id) {
  const response = await fetch(`${ApiBase}api/settings/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) return data.results[0];
}
export async function getCoursesCount() {
  const response = await fetch(`${ApiBase}api/course`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.count) {
    return data.count;
  } else return 0;
}
export async function loadCourseParticipants(course_id, page,size=10,filter_query) {
  
  var query = `?page=${page}&size=${size}`
  if(course_id)
    query += `&course=${course_id}`
  if(filter_query)
  query += filter_query

  const response = await fetch(
    `${ApiBase}api/courseparticipant/${query}`,
    {
      //${course_id}
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}

export async function loadCourseParticipantsByQuery(query) {
  
  console.log(query)

  const response = await fetch(
    `${ApiBase}api/courseparticipant/${query}`,
    {
      //${course_id}
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}

export async function updateCourseParticipantFavorite(id, favorite) {
  let body = {
    favorite : favorite,
  };
  const response = await fetch(`${ApiBase}api/courseparticipant/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function loadCourseParticipantsCount(course_id) {
  const response = await fetch(
    `${ApiBase}api/courseparticipant/?course=${course_id}`,
    {
      //
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.count) {
    return data.count;
  } else return 0;
}
export async function loadCourseParticipantsByUser(user_id) {
  const response = await fetch(
    `${ApiBase}api/courseparticipant/?user=${user_id}`,
    {
      //
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}
export async function loadCountries(page, size = 10) {
  var query_text = "?size=" + size + "&language_id=" + default_language_id;
  if (page) query_text += "&page=" + page;

  const response = await fetch(`${ApiBase}base/country${query_text}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    var coutries = data.results;
    return data.results;
  }
}
export async function loadCountriesWithCount(page) {
  var page_text = "";
  if (page) page_text = "/?page=" + page;

  const response = await fetch(`${ApiBase}base/country${page_text}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadCitiesWithCount(country_id, page = 1) {
  var page_text = "";
  if (page) page_text = "/?page=" + page;

  const response = await fetch(
    `${ApiBase}base/country/${country_id}/cities?size=1000000` +
      "&language_id=" +
      default_language_id,
    {
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}
export async function loadCities(country_id) {
  const response = await fetch(
    `${ApiBase}base/country/${country_id}/cities?size=1000000` +
      "&language_id=" +
      default_language_id,
    {
      headers: createCmsJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}
export async function loadCity(id) {
  const response = await fetch(`${ApiBase}base/city/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadLanguage(id) {
  const response = await fetch(`${ApiBase}base/language/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadUser(id) {
  const response = await fetch(`${ApiBase}auth/user/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadUserTalent(id) {
  const response = await fetch(`${ApiBase}api/usertalent/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadTalent(id) {
  const response = await fetch(`${ApiBase}api/talent/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadTalentDocument(document_id) {
  const response = await fetch(`${ApiBase}api/document/${document_id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadQuestion(question_id) {
  const response = await fetch(`${ApiBase}api/question/${question_id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadCourse(id) {
  const response = await fetch(`${ApiBase}api/course/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadCountry(id) {
  const response = await fetch(`${ApiBase}base/country/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function saveLanguage(language) {
  const method = language.id ? "PUT" : "POST";
  let body = {
    name: language.name,
    abbreviation: language.abbreviation,
    is_default: language.is_default,
  };
  if (language.id) {
    body = {
      id: language.id,
      name: language.name,
      abbreviation: language.abbreviation,
      is_default: language.is_default,
    };
  }

  const response = await fetch(
    `${ApiBase}base/language/${language.id ? language.id + "/" : ""}`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function saveTranslate(translate) {
  const method = translate.id ? "PUT" : "POST";
  let body = {
    key: translate.key,
    values: translate.values,
  };

  const response = await fetch(
    `${ApiBase}api/translate/${translate.id ? translate.id  : ""}`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  return data
}

export async function uploadTalentHeader(talent_id, file) {
  const formData = new FormData();

  formData.append("header", file, file.name);
  const response = await fetch(`${ApiBase}api/talent/${talent_id}/`, {
    headers: createCmsFileHeaders(),
    method: "PATCH",
    body: formData,
  });

  const data = await response.json();

  return data;
}

export async function uploadUsertalentDocument(
  usertalent_id,
  document_id,
  url,
  file,
) {
  let formData = new FormData();
  formData.append("document", document_id);
  formData.append("url", url);


  if (file) formData.append("file", file, file.name);

  const response = await fetch(
    `${ApiBase}api/usertalent/${usertalent_id}/userdocuments/`,
    {
      headers: createCmsFileHeaders(),
      method: "POST",
      body: formData,
    }
  ).catch((error) => {
    console.log(error);
  });

  const data = (await response) ? response.json() : null;

  return data;
}
export async function uploadTalentImage(talent_id, file) {
  if (!talent_id) return;

  const formData = new FormData();

  formData.append("image", file, file.name);
  const response = await fetch(`${ApiBase}api/talent/${talent_id}/`, {
    headers: createCmsFileHeaders(),
    method: "PATCH",
    body: formData,
  });

  const data = await response.json();

  return data;
}
export async function uploadCourseImage(course_id, file) {
  if (!course_id) return;

  const formData = new FormData();

  formData.append("image", file, file.name);
  const response = await fetch(`${ApiBase}api/course/${course_id}/`, {
    headers: createCmsFileHeaders(),
    method: "PATCH",
    body: formData,
  });

  const data = await response.json();

  return data;
}
export async function SaveGallery(title, file) {
  const formData = new FormData();
  formData.append("title", title);
  formData.append("image", file, file.name);
  const response = await fetch(`${ApiBase}postapi/gallery/`, {
    headers: createCmsFileHeaders(),
    method: "POST",
    body: formData,
  });

  const data = await response.json();

  return data;
}
export async function saveTalent(talent) {
  const method = talent.id ? "PUT" : "POST";
  let body = {
    names: talent.names,
    parent: talent.parent,
    status: talent.status,
    menu: talent.menu,
    header_url: talent.header_url,
    image_url: talent.image_url,
  };
  if (talent.academy_courses !== undefined) {
    body.academy_courses = talent.academy_courses;
  }

  const response = await fetch(
    `${ApiBase}api/talent/${talent.id ? talent.id + "/" : ""}`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  clearCachedTalents();
  if (data) {
    return data;
  }
}

export async function patchTalent(id, body) {
  const response = await fetch(`${ApiBase}api/talent/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.detail || data.error || "Request failed");
    err.body = data;
    throw err;
  }
  return data;
}
export async function uploadImage(file) {
  const formData = new FormData();

  formData.append("image", file, file.name);
  const response = await fetch(`${ApiBase}api/upload`, {
    headers: createCmsFileHeaders(),
    method: "PATCH",
    body: formData,
  });

  const data = await response.json();

  return data;
}
export async function savePost(
  id,
  title,
  short_description,
  description,
  keywords,
  is_featured,
  is_featured_in_blog,
  status,
  category_id,
  talent_id,
  language_id,
  file,
  gallery_id,
  video_url
) {
  const formData = new FormData();

  formData.append("title", title);
  formData.append("short_description", short_description);
  formData.append("description", description);
  if (is_featured) formData.append("is_featured", is_featured);
  if (is_featured) formData.append("is_featured_in_blog", is_featured_in_blog);
  formData.append("keywords", keywords);
  if (category_id) formData.append("category_id", category_id);
  formData.append("title", title);
  if (status) formData.append("status", status);

  if (talent_id) formData.append("talent", talent_id);
  formData.append("language", language_id);
  if (gallery_id) formData.append("gallery_image", gallery_id);
  if (video_url != null) formData.append("video_url", video_url);

  if (file) formData.append("image", file, file.name);

  var response;
  if (id)
    response = await fetch(`${ApiBase}postapi/post/${id + "/"}`, {
      headers: createCmsFileHeaders(),
      method: "PUT",
      body: formData,
    });
  else
    response = await fetch(`${ApiBase}postapi/language/${language_id}/posts/`, {
      headers: createCmsFileHeaders(),
      method: "POST",
      body: formData,
    });

  const data = await response.json();

  return data;
}

export async function savePage(
  id,
  title,
  url,
  short_description,
  description,
  keywords,
  is_featured,
  status,
  language_id,
  gallery_id
) {
  const formData = new FormData();

  formData.append("title", title);
  formData.append("url", url);
  formData.append("short_description", short_description);
  formData.append("description", description);
  if (is_featured) formData.append("is_featured", is_featured);
  formData.append("keywords", keywords);
  if (status) formData.append("status", status);

  formData.append("language", language_id);
  if (gallery_id) formData.append("gallery_image", gallery_id);

  var response = await fetch(`${ApiBase}postapi/page/${id ? id + "/" : ""}`, {
    headers: createCmsFileHeaders(),
    method: id ? "PUT" : "POST",
    body: formData,
  });

  const data = await response.json();

  return data;
}

export async function saveSettings(
  id,
  title,
  meta_description,
  meta_keywords,
  facebook,
  instagram,
  youtube,
  spotify,
  linkedin,
  twitter,
  image,
  slideshow_images = null
) {
  const formData = new FormData();

  formData.append("title", title);
  formData.append("meta_description", meta_description);
  formData.append("meta_keywords", meta_keywords);
  formData.append("facebook", facebook);
  formData.append("instagram", instagram);
  formData.append("youtube", youtube);
  formData.append("spotify", spotify);
  formData.append("linkedin", linkedin);
  formData.append("twitter", twitter);
  if (slideshow_images !== null && slideshow_images !== undefined) {
    formData.append("slideshow_images", slideshow_images);
  }

  if (image) formData.append("image", image, image.name);

  var response;
  if (id)
    response = await fetch(`${ApiBase}api/settings/${id}`, {
      headers: createCmsFileHeaders(),
      method: "PUT",
      body: formData,
    });
  else
    response = await fetch(`${ApiBase}api/settings/`, {
      headers: createCmsFileHeaders(),
      method: "POST",
      body: formData,
    });

  const data = await response.json();

  return data;
}

export async function saveSettingsSlideshow(id, slideshow_images) {
  const formData = new FormData();
  formData.append("slideshow_images", slideshow_images || "[]");

  let response;
  if (id) {
    response = await fetch(`${ApiBase}api/settings/${id}`, {
      headers: createCmsFileHeaders(),
      method: "PUT",
      body: formData,
    });
  } else {
    response = await fetch(`${ApiBase}api/settings/`, {
      headers: createCmsFileHeaders(),
      method: "POST",
      body: formData,
    });
  }

  return response.json();
}

export async function saveAbout(
  settings,
  names,
  about_title,
  about,
  about_image
) {
  const formData = new FormData();

  if (!settings) return;

  formData.append("about_title", about_title);
  formData.append("about", about);
  if (names) saveAboutNames(settings.id, names);

  if (settings.about_icons_title_1)
    formData.append("about_icons_title_1", settings.about_icons_title_1);
  if (settings.about_icons_title_2)
    formData.append("about_icons_title_2", settings.about_icons_title_2);
  if (settings.about_icons_title_3)
    formData.append("about_icons_title_3", settings.about_icons_title_3);
  if (settings.about_icons_title_4)
    formData.append("about_icons_title_4", settings.about_icons_title_4);

  if (settings.about_icons_description_1)
    formData.append(
      "about_icons_description_1",
      settings.about_icons_description_1
    );
  if (settings.about_icons_description_2)
    formData.append(
      "about_icons_description_2",
      settings.about_icons_description_2
    );
  if (settings.about_icons_description_3)
    formData.append(
      "about_icons_description_3",
      settings.about_icons_description_3
    );
  if (settings.about_icons_description_4)
    formData.append(
      "about_icons_description_4",
      settings.about_icons_description_4
    );

  if (settings.about_circle_image_1)
    formData.append("about_circle_image_1", settings.about_circle_image_1);
  if (settings.about_circle_image_2)
    formData.append("about_circle_image_2", settings.about_circle_image_2);
  if (settings.about_circle_image_3)
    formData.append("about_circle_image_3", settings.about_circle_image_3);
  if (settings.about_circle_image_4)
    formData.append("about_circle_image_4", settings.about_circle_image_4);

  if (settings.about_image)
    formData.append("about_image", settings.about_image);

  var response;
  if (settings.id)
    response = await fetch(`${ApiBase}api/settings/${settings.id}`, {
      headers: createCmsFileHeaders(),
      method: "PUT",
      body: formData,
    });
  else
    response = await fetch(`${ApiBase}api/settings/`, {
      headers: createCmsFileHeaders(),
      method: "POST",
      body: formData,
    });

  const data = await response.json();

  return data;
}

export async function saveAboutNames(id, names) {
  const method = id ? "PUT" : "POST";
  let body = {
    names: names,
  };

  console.log(names);

  const response = await fetch(`${ApiBase}api/settings/${id ? id : ""}`, {
    headers: createCmsJsonHeaders(),
    method: method,
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function saveCategory(category) {
  const method = category.id ? "PUT" : "POST";
  let body = {
    title: category.title,
    parent: category.parent,
    status: category.status,
    is_featured: category.featured,
  };

  if (category.id) {
    body["id"] = category.id
  }

  const response = await fetch(
    `${ApiBase}postapi/category/${category.id ? category.id + "/" : ""}`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  return data;
}
export async function saveTalentDocument(talent_id, talentDocument) {
  console.log(talentDocument);
  const method = talentDocument.id ? "PUT" : "POST";
  let body = {
    names: talentDocument.names,
    max_docs: talentDocument.max_docs,
    extensions: talentDocument.extensions,
    max_file_size: talentDocument.max_file_size,
  };
  if (talentDocument.id) {
    body = {
      id: talentDocument.id,
      names: talentDocument.names,
      max_docs: talentDocument.max_docs,
      extensions: talentDocument.extensions,
      max_file_size: talentDocument.max_file_size,
    };
  }

  //console.log(JSON.stringify(talentDocument))

  var response;
  if (talentDocument.id) {
    response = await fetch(`${ApiBase}api/document/${talentDocument.id}/`, {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    });
  } else
    response = await fetch(`${ApiBase}api/talent/${talent_id}/documents/`, {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    });

  const data = await response.json();

  return data;
}

export async function saveTalentQuestion(talent_id, talentQuestion) {
  const method = talentQuestion.id ? "PUT" : "POST";
  let body = {
    names: talentQuestion.names,
    weight: talentQuestion.weight,
  };

  if (talentQuestion.id) {
    body = {
      id: talentQuestion.id,
      names: talentQuestion.names,
      weight: talentQuestion.weight,
    };

    var response = await fetch(`${ApiBase}api/question/${talentQuestion.id}/`, {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    });
    const data = await response.json();

    return data;
  } else {
    const response = await fetch(
      `${ApiBase}api/talent/${talent_id}/questions/`,
      {
        headers: createCmsJsonHeaders(),
        method: method,
        body: JSON.stringify(body),
      }
    );

    const data = await response.json();

    return data;
  }
}

export async function saveCourse(course) {
  if (course.id) return editCourse(course);
  else return addCourse(course);
}

async function editCourse(course) {
  const method = "PUT";
  let body = {
    names: course.names,
    image: course.image,
    talents: course.talents,
    status: course.status,
    staff_preview: !!course.staff_preview,
    country: course.country,
    startdate:
      course.startdate.year +
      "-" +
      course.startdate.month +
      "-" +
      course.startdate.day,
    enddate:
      course.enddate.year +
      "-" +
      course.enddate.month +
      "-" +
      course.enddate.day,
  };

  const response = await fetch(`${ApiBase}api/course/${course.id}/`, {
    headers: createCmsJsonHeaders(),
    method: method,
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (data) {
    return data;
  }
}
async function addCourse(course) {
  const method = "POST";
  const talentList = course.talents && course.talents.length ? course.talents : [];
  const firstTalent = talentList[0];
  if (!firstTalent) {
    return Promise.reject(new Error("At least one talent is required"));
  }
  let body = {
    names: course.names,
    status: course.status,
    staff_preview: !!course.staff_preview,
    talents: talentList,
    image: course.image,
    country: course.country,
    startdate:
      course.startdate.year +
      "-" +
      course.startdate.month +
      "-" +
      course.startdate.day,
    enddate:
      course.enddate.year +
      "-" +
      course.enddate.month +
      "-" +
      course.enddate.day,
  };

  const response = await fetch(
    `${ApiBase}api/talent/${firstTalent}/courses/`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  if (data) {
    return data;
  }
}
export async function saveCountry(country) {
  const method = country.id ? "PUT" : "POST";
  let body = {
    names: country.names,
    code: country.code,
  };
  if (country.id) {
    body = {
      id: country.id,
      names: country.names,
      code: country.code,
    };
  }

  const response = await fetch(
    `${ApiBase}base/country/${country.id ? country.id + "/" : ""}`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function saveCity(city, country_id) {
  console.log(city.status);

  let response;
  let body;

  if (city.id) {
    body = {
      id: city.id,
      names: city.names,
      status: city.status,
    };
    response = await fetch(`${ApiBase}base/city/${city.id}/`, {
      headers: createCmsJsonHeaders(),
      method: "PUT",
      body: JSON.stringify(body),
    });
  } else {
    body = {
      names: city.names,
      status: city.status,
    };
    response = await fetch(`${ApiBase}base/country/${country_id}/cities/`, {
      headers: createCmsJsonHeaders(),
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  const data = await response.json();
  return data;
}
export async function saveUser(user) {
  const method = user.id ? "PUT" : "POST";
  let body = {
    first_name: user.first_name,
    last_name: user.last_name,
    gender: user.gender ? user.gender : 0,
    phone_number: user.phone_number ? user.phone_number : "",
    birth_date: user.birth_date,
    country_id: user.country_id,
    city_id: user.city_id,
    livingcountry_id: user.livingcountry_id,
    livingcity_id: user.livingcity_id,
    website: user.website ? user.website : "",
    facebook: user.facebook ? user.facebook : "",
    instagram: user.instagram ? user.instagram : "",
    youtube: user.youtube ? user.youtube : "",
    snapchat: user.snapchat ? user.snapchat : "",
    spotify: user.spotify ? user.spotify : "",
    home_address: "",
    education_degree : "",
    education_field : "",
    is_active: user.is_active !== false,
    is_verified: user.is_verified !== false,
    is_test_user: !!user.is_test_user,
  };

  if(user.password) {
    body["password"] = user.password
  }

  if (user.id!=null) {

    const response = await fetch(
      `${ApiBase}auth/profiles/${user.id ? user.id + "/" : ""}`,
      {
        headers: createCmsJsonHeaders(),
        method: method,
        body: JSON.stringify(body),
      }
    ); 

    const data = await response.json();


    if (data.results) {
      return data.results;
    }
  } else {
    // Create new user
    let body = {
      first_name: user.first_name,
      last_name: user.last_name,
      gender: user.gender ? user.gender : 0,
      email: user.email,
      password: user.password,
      is_verified: user.is_verified ? user.is_verified : true,
      role: user.role ? user.role : "participant",
      phone_number: user.phone_number ? user.phone_number : "",
      birth_date: user.birth_date,
      country_id: user.country_id,
      city_id: user.city_id,
      livingcountry_id: user.livingcountry_id,
      livingcity_id: user.livingcity_id,
      website: user.website ? user.website : "",
      facebook: user.facebook ? user.facebook : "",
      instagram: user.instagram ? user.instagram : "",
      youtube: user.youtube ? user.youtube : "",
      snapchat: user.snapchat ? user.snapchat : "",
      spotify: user.spotify ? user.spotify : "",
      talent_id: user.talent,
      is_active: user.is_active !== false,
      is_verified: user.is_verified !== false,
      is_test_user: !!user.is_test_user,
    };
    const response = await fetch(`${ApiBase}auth/user/new/`, {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    });

    const data = await response.json();

    return data;
  }
}

export async function blockUser(user) {
  if (!user) return;

  const method = user.id ? "PUT" : "POST";
  let body = {
    first_name: user.first_name,
    last_name: user.last_name,
    is_active: user.is_active ? 0 : 1,
  };

  const response = await fetch(
    `${ApiBase}auth/profiles/${user.id ? user.id + "/" : ""}`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  console.log(data);
  return data;
}
export async function uploadProfileImage(user, file) {
  if (!user) return;

  const formData = new FormData();

  formData.append("image", file, file.name);
  formData.append("first_name", user.first_name);
  formData.append("last_name", user.last_name);

  const method = "PUT";

  const response = await fetch(
    `${ApiBase}auth/profiles/${user.id ? user.id + "/" : ""}`,
    {
      headers: createCmsFileHeaders(),
      method: method,
      body: formData,
    }
  );

  const data = await response.json();

  console.log(data);
  return data;
}
export async function saveReferee(user, country_id, city_id,bio) {
  const method = user.user ? "PUT" : "POST";
  let body = {
    first_name: user.first_name,
    last_name: user.last_name,
    gender: user.gender ? user.gender : 0,
    email: user.email,
    country_id: country_id,
    bio: bio,
    city_id: city_id,
  };

  if (user.password) body = { ...body, password: user.password };

  var request = `${ApiBase}auth/registerreferee/`;

  if (user.id > 0)
    request = `${ApiBase}auth/profiles/${user.id ? user.id + "/" : ""}`;
  console.log(request);
  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: method,
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function saveAdministrator(user, country_id, city_id) {
  const method = user.user ? "PUT" : "POST";
  let body = {
    first_name: user.first_name,
    last_name: user.last_name,
    gender: user.gender ? user.gender : 0,
    email: user.email,
    country_id: country_id,
    city_id: city_id,
  };

  if (user.password) body = { ...body, password: user.password };

  console.log(JSON.stringify(body));

  var request = `${ApiBase}auth/registeradmin/`;

  if (user.id > 0)
    request = `${ApiBase}auth/profiles/${user.id ? user.id + "/" : ""}`;
  console.log(request);
  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: method,
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function saveEditor(user, country_id, city_id) {
  const method = user.user ? "PUT" : "POST";
  let body = {
    first_name: user.first_name,
    last_name: user.last_name,
    gender: user.gender ? user.gender : 0,
    email: user.email,
    country_id: country_id,
    city_id: city_id,
  };

  if (user.password) body = { ...body, password: user.password };

  console.log(JSON.stringify(body));

  var request = `${ApiBase}auth/registereditor/`;

  if (user.id > 0)
    request = `${ApiBase}auth/profiles/${user.id ? user.id + "/" : ""}`;
  console.log(request);
  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: method,
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function addRefereeToCourse(referee_id, course_id) {
  let body = {
    course: course_id,
    user: referee_id,
  };
  var request = `${ApiBase}api/coursereferee/`;

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function setTalentSortOrder(ids) {
  let body = {
    ids: ids,
  };
  var request = `${ApiBase}api/talent/setsortorder/`;

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function saveQuestionssSortOrder(ids) {
  let body = {
    ids: ids,
  };
  var request = `${ApiBase}api/talent/question/sortorder`;

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function deleteLanguage(id) {
  const response = await fetch(`${ApiBase}base/language/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}


export async function deleteMessage(id) {
  const response = await fetch(`${ApiBase}api/supportmessages/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}


export async function deleteRequest(api,id) {
  const response = await fetch(`${ApiBase}${api}/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}
export async function deleteGallery(id) {
  const response = await fetch(`${ApiBase}postapi/gallery/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}

export async function deleteVideoGallery(id) {
  await fetch(`${ApiBase}postapi/videogallery/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}

export async function deleteCategory(id) {
  const response = await fetch(`${ApiBase}postapi/category/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}
export async function deletePost(id) {
  const response = await fetch(`${ApiBase}postapi/post/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}
export async function deletePage(id) {
  const response = await fetch(`${ApiBase}postapi/page/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}
export async function deleteFaq(id) {
  const response = await fetch(`${ApiBase}api/faq/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}
export async function deleteTestimonial(id) {
  const response = await fetch(`${ApiBase}api/testimonials/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}

export async function deleteEmail(id) {
  const response = await fetch(`${ApiBase}api/emails/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}

export async function deleteNotification(id) {
  const response = await fetch(`${ApiBase}api/notifications/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}
export async function deleteCountry(id) {
  const response = await fetch(`${ApiBase}base/country/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}
export async function deleteCity(id) {
  const response = await fetch(`${ApiBase}base/city/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}

export async function deleteUserTalent(id) {
  const response = await fetch(`${ApiBase}api/usertalent/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
  if (!response.ok) throw new Error(response.statusText);

  await response;
}

export async function deleteUser(id) {
  const response = await fetch(`${ApiBase}auth/user/del/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
  await response;
}
export async function deleteCourse(id) {
  const response = await fetch(`${ApiBase}api/course/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
  await response;
}
export async function deleteCourseParticipant(id) {
  const response = await fetch(`${ApiBase}api/courseparticipant/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
  await response;
}
export async function deleteCourseReferee(id) {
  const response = await fetch(`${ApiBase}api/coursereferee/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
  await response;
}

export async function deleteTalent(id) {
  const response = await fetch(`${ApiBase}api/talent/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
  await response;
  clearCachedTalents();
}
export async function deleteTalentDocument(id) {
  const response = await fetch(`${ApiBase}api/document/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
  await response;
}

export async function deleteQuesion(id) {
  const response = await fetch(`${ApiBase}api/question/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
  await response;
}

export async function loadTestimonials(filter_query, page) {
  if (page) filter_query += "&page=" + page;

  console.log(filter_query);

  const response = await fetch(`${ApiBase}api/testimonials/${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadTestimonial(id) {
  const response = await fetch(`${ApiBase}api/testimonials/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function saveTestimonial(id, title, name, description, language) {
  const method = id ? "PUT" : "POST";
  let body = {
    title: title,
    name: name,
    description: description,
    language: language,
  };

  const response = await fetch(`${ApiBase}api/testimonials/${id ? id : ""}`, {
    headers: createCmsJsonHeaders(),
    method: method,
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (data) {
    return data;
  }
}
export async function loadNotifications(id = "") {
  const response = await fetch(`${ApiBase}api/notifications/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}


export async function loadMessages(filter_query="", page) {
  if (page) filter_query += "&page=" + page;

  const response = await fetch(`${ApiBase}api/supportmessages/${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}


export async function loadeEmails(page,to="") {
  var filter_query="?size=10";
  if (page) filter_query += "&page=" + page;
  if (to) filter_query += "&to=" + to;

  const response = await fetch(`${ApiBase}api/emails/${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadEmail(id) {
  const response = await fetch(`${ApiBase}api/emails/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadMessage(id) {
  const response = await fetch(`${ApiBase}api/supportmessages/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadMessageReplies(message_id, page) {
  var filter_query = "";
  if (page) filter_query = "?page=" + page;

  const response = await fetch(`${ApiBase}api/supportmessages/${message_id}/replies${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function saveReply(supportmessage_id, message) {
  let body = {
    message: message,
  };

  const response = await fetch(`${ApiBase}api/supportmessages/${supportmessage_id}/replies`, {
    headers: createCmsJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function sendEmails(emails,talent,subject, message) {
  let body = {
    emails: emails,
    subject: subject,
    message: message,
  };

  if(talent)
    body['talent']= talent;

  const response = await fetch(`${ApiBase}api/email/`, {
    headers: createCmsJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function saveFaq(id, question, answer, status, language, names = []) {
  const method = id ? "PUT" : "POST";
  let body = {
    question: question,
    answer: answer,
    language: language,
    status: status ? 1 : 0,
    names: names,
  };


  const response = await fetch(
    `${ApiBase}api/faq/${id ? id + "" : ""}`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  console.log(data)

  return data;
}
export async function setFaqSortOrder(ids) {
  let body = {
    ids: ids,
  };
  var request = `${ApiBase}api/faq/setsortorder/`;

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();


  return data;
}
///////////////////////////////////////////

export async function deleteItem(url,id) {
  const response = await fetch(`${ApiBase}${url}/${id}`, {
    headers: createCmsJsonHeaders(),
    method: "DELETE",
  });
}

///////////////////////////////////////////


export async function loadVideoGalleries(filter_query = "", page) {
  if (filter_query) {
    if (page) filter_query += "&page=" + page;
  } else if (page) filter_query += "?page=" + page;

  const response = await fetch(`${ApiBase}postapi/videogallery/${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}


export async function setVideoGallerySortOrder(ids) {
  let body = {
    ids: ids,
  };
  var request = `${ApiBase}postapi/videogallery/setsortorder/`;

  const response = await fetch(request, {
    headers: createCmsJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();


  return data;
}

export async function loadVideoGallery(id) {
  const response = await fetch(`${ApiBase}postapi/videogallery/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function saveVideoGallery(
  id,
  title,
  language_id,
  image,
  tag,
  file,
) {
  const formData = new FormData();

  formData.append("title", title);
  formData.append("tag", tag);
  formData.append("language", language_id);
  formData.append("image", image);
  
  if (file) formData.append("file", file, file.name);

  var response;
  if (id)
    response = await fetch(`${ApiBase}postapi/videogallery/${id}/`, {
      headers: createCmsFileHeaders(),
      method: "PATCH",
      body: formData,
    });
  else
    response = await fetch(`${ApiBase}postapi/videogallery/`, {
      headers: createCmsFileHeaders(),
      method: "POST",
      body: formData,
    });

  const data = await response.json();

  return data;
}

///////////////////////////////////////////

export async function saveUserTalentGallery(
  id,
  usertalent,
  url,
  coursehistory,
  period,
  file = null,
) {
  const formData = new FormData();

  formData.append("url", url);
  formData.append("usertalent", usertalent);
  formData.append("coursehistory", coursehistory);
  formData.append("period", period);
  
  if (file) formData.append("file", file, file.name);

  var response;
  if (id)
    response = await fetch(`${ApiBase}api/usertalentgallery/${id}/`, {
      headers: createCmsFileHeaders(),
      method: "PATCH",
      body: formData,
    });
  else
    response = await fetch(`${ApiBase}api/usertalentgallery/`, {
      headers: createCmsFileHeaders(),
      method: "POST",
      body: formData,
    });

  const data = await response.json();

  return data;
}

export async function deleteUserTalentDocument(userdocument) {

   if(userdocument.url?.includes("firebasestorage")){
     const storage = getStorage(app);
     var desertRef = ref(storage, userdocument.url); 
  
  //console.log(userdocument.url);

    deleteObject(desertRef).then(function() {
      // File deleted successfully
      console.log("File deleted successfully")
    }).catch(function(error) {
      // Uh-oh, an error occurred!
    });
   }

  const response = await fetch(
    `${ApiBase}api/userdocument/${userdocument.id}`,
    {
      headers: createCmsJsonHeaders(),
      method: "DELETE",
    }
  );
}

///////////////////////////////////////////

export async function addParticipantToCourse(
  course_id,
  usertalent_id,
  userdocument_ids
) {
  let body = {
    course: course_id,
    usertalent: usertalent_id,
    userdocument_ids: userdocument_ids,
  };
  console.log(body);
  const response = await fetch(`${ApiBase}api/courseparticipant/`, {
    headers: createCmsJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

///////////////////////////////////////////

export async function loadPopupMessages(filter_query = "", page) {
  if (filter_query) {
    if (page) filter_query += "&page=" + page;
  } else if (page) filter_query += "?page=" + page;

  const response = await fetch(`${ApiBase}postapi/popupmessage/${filter_query}`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadPopupMessage(id) {
  const response = await fetch(`${ApiBase}postapi/popupmessage/${id}/`, {
    headers: createCmsJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}


export async function savePopupMessage(id,url,description,language,image) {
  const method = id ? "PUT" : "POST";
  let body = {
    description: description,
    url: url,
    language: language,
    image: image,
  };

  const response = await fetch(
    `${ApiBase}postapi/popupmessage/${id ? id + "/" : ""}`,
    {
      headers: createCmsJsonHeaders(),
      method: method,
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  if (data) {
    return data;
  }
}

///////////////////////////////////////////


// This file was used to send api requests until Wednesday, June 8 2022, 
// We user httpService class in hhtp folder from now on to send requests.